import {useState} from 'react';

import {CheckBox, ExpandToggle} from '@/components';
import type {BusinessAllowedFeature} from '@/types/AfriexBusiness';

interface FeatureNodeProps {
  feature: BusinessAllowedFeature;
  onToggle: (key: string, isSelected: boolean) => void;
  disabled?: boolean;
}

export const AllowedFeatureNode: React.FC<FeatureNodeProps> = ({
  feature,
  onToggle,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const children = feature?.children;
  const hasChildren = !!(children && children.length > 0);

  return (
    <div className="w-full">
      {/* Row Container */}
      <div
        data-cy="business-details-allowed-features-row"
        className="flex items-center py-1.5 gap-3 group"
      >
        <CheckBox
          dataCy="business-details-allowed-features"
          checked={!!feature?.isSelected}
          disabled={disabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onToggle(feature.key!, event.target.checked);
          }}
        />

        <span
          data-cy="business-details-allowed-features-label"
          className="flex-1 text-sm font-medium text-slate-700 cursor-default"
          onClick={() => hasChildren && setIsOpen(!isOpen)}
        >
          {feature.label || feature.key}
        </span>

        {hasChildren && (
          <ExpandToggle
            dataCy="business-details-allowed-features-expand"
            isOpen={isOpen}
            onClick={() => setIsOpen(!isOpen)}
          />
        )}
      </div>

      {/* Nested Children with Animation-ready Container */}
      {hasChildren && isOpen && children && (
        <div className="pl-5 ml-2 border-l dark:border-l-dark-borderMid">
          {children.map((child) => (
            <AllowedFeatureNode
              key={child.key}
              feature={child}
              onToggle={onToggle}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};
